import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { MarketPlaceTradeAdvise } from "@lib/types/MarketPlace";
import { TableColumnsProps } from "@lib/types/General";
import { IntlShape } from "react-intl";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";
import { AppTag } from "@lib/components/AppComponents";
import {
  tradeAdviseActionColor,
  tradeAdviseMarketBiasColor,
  tradeAdviseRiskLevelColor,
  tradeAdviseTypeColor,
  tradeAdviseHoldingPeriodColor,
} from "@lib/helpers/ColorHelper";

type Props<T> = TableColumnsProps<T> & {
  handleSelectItem: (item: T) => void;
  messages: IntlShape["messages"];
};

const getColumns = <T extends MarketPlaceTradeAdvise>(
  props: Props<T>,
): ColumnsType<T> => {
  const { handleSelectItem } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
      width: "180px",
      filterType: "search",
    },
    {
      title: "common.symbol",
      dataIndex: "symbol",
      key: "symbol",
      fixed: "left",
      width: "120px",
      filterType: "search",
      render: (text) => <AppTag color="blue">{text}</AppTag>,
    },
    {
      title: "common.action",
      dataIndex: "action",
      key: "action",
      width: "130px",
      filterType: "search",
      render: (text) => (
        <AppTag color={tradeAdviseActionColor(text)}>{text}</AppTag>
      ),
    },
    {
      title: "common.type",
      dataIndex: "type",
      key: "type",
      width: "110px",
      filterType: "search",
      render: (text) => (
        <AppTag color={tradeAdviseTypeColor(text)}>{text}</AppTag>
      ),
    },
    {
      title: "common.marketBias",
      dataIndex: "marketBias",
      key: "marketBias",
      width: "120px",
      filterType: "search",
      render: (text) => (
        <AppTag color={tradeAdviseMarketBiasColor(text)}>{text}</AppTag>
      ),
    },
    {
      title: "common.riskLevel",
      dataIndex: "riskLevel",
      key: "riskLevel",
      width: "120px",
      filterType: "dropdown",
      render: (text) => (
        <AppTag color={tradeAdviseRiskLevelColor(text)}>{text}</AppTag>
      ),
    },
    {
      title: "common.holdingPeriod",
      dataIndex: "holdingPeriod",
      key: "holdingPeriod",
      width: "130px",
      filterType: "dropdown",
      render: (text) => (
        <AppTag color={tradeAdviseHoldingPeriodColor(text)}>{text}</AppTag>
      ),
    },
    {
      title: "common.confidence",
      dataIndex: "confidence",
      key: "confidence",
      width: "110px",
      render: (text) => (text ? `${text}%` : "-"),
    },
    {
      title: "common.takeProfit",
      dataIndex: "takeProfit",
      key: "takeProfit",
      width: "110px",
    },
    {
      title: "common.stopLoss",
      dataIndex: "stopLoss",
      key: "stopLoss",
      width: "110px",
    },
    {
      title: "common.volume",
      dataIndex: "volume",
      key: "volume",
      width: "90px",
    },
    {
      title: "common.language",
      dataIndex: "language",
      key: "language",
      width: "120px",
      render: (d) => (d ? <LanguageLabel languageCode={d} /> : "-"),
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "150px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      key: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelectItem(record)}
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
